import React, { useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import { resetDocumentThemeOverrides } from './themeReset';
import { TabBarVisibilityProvider } from './contexts/TabBarVisibilityContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import {
  OnboardingPage,
  MainPage,
  CartPage,
  FittingPage,
  TryOnGalleryPage,
  FeedPage,
  CatalogPage,
  CatalogItemPage,
  ItemReviewsPage,
  ProfilePage,
  LikedPage,
  AddressesPage,
  AddressEditPage,
  CdekPickupStubPage,
  CheckoutPage,
  OrdersPage,
  LeaveReviewPage,
  PolicyPage,
  PublicOfferPage,
  ReferralDashboardPage,
  SessionsPage,
  BrowserLoginPage,
  BrowserLoginQrPage,
  LinkAccountPage,
  SupportChatPage,
} from './pages';
import { markStartappItemRoot } from './utils/startappItemEntry';
import { isTelegramWebAppEnvironment, hasTelegramWebAppInitData } from './utils/telegramEnvironment';
import RequireWebUser from './components/RequireWebUser/RequireWebUser';
import { initProductAnalytics, shutdownProductAnalytics } from './utils/productAnalytics';
// ANCHOR: vk-group-announcement-modal — import (удалить вместе с <VkGroupAnnouncementBoot /> ниже)
import { VkGroupAnnouncementBoot } from './components/VkGroupAnnouncementModal';

function _readTgStartParamFromLocation() {
  const hash = window.location.hash.slice(1);
  const qIdx = hash.indexOf('?');
  const queryFromHash = qIdx >= 0 ? hash.slice(qIdx + 1) : '';
  const params = new URLSearchParams(queryFromHash);
  let startParam = params.get('tgWebAppStartParam') || '';
  if (!startParam && typeof window.location.search === 'string' && window.location.search.length > 1) {
    const p2 = new URLSearchParams(window.location.search.slice(1));
    startParam = p2.get('tgWebAppStartParam') || '';
  }
  if (!startParam && isTelegramWebAppEnvironment()) {
    startParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param || '';
  }
  return startParam;
}

/** При первом открытии по ссылке t.me/bot?startapp=item_123 — редирект на страницу товара. Выполняется один раз за сессию, чтобы клик по лого вёл на ленту, а не снова на товар. */
function useStartParamRedirect() {
  const navigate = useNavigate();
  const didRedirectRef = React.useRef(false);
  useEffect(() => {
    if (didRedirectRef.current) return;
    const tryRedirect = () => {
      if (didRedirectRef.current) return false;
      const startParam = _readTgStartParamFromLocation();
      const m = /^item_(\d+)$/.exec(startParam);
      if (m) {
        didRedirectRef.current = true;
        const itemId = m[1];
        markStartappItemRoot(itemId);
        navigate(`/main/catalog/${itemId}`, { replace: true, state: { fromStartappItem: true } });
        return true;
      }
      return false;
    };
    if (tryRedirect()) return undefined;
    let n = 0;
    const id = setInterval(() => {
      n += 1;
      if (tryRedirect() || n >= 25) {
        clearInterval(id);
      }
    }, 120);
    return () => clearInterval(id);
  }, [navigate]);
}

function RootRedirect() {
  const { user, isPolicyAccepted } = useAuth();
  if (!user && !hasTelegramWebAppInitData()) {
    return <Navigate to="/main/catalog" replace />;
  }
  if (user && !isPolicyAccepted) {
    return <Navigate to="/onboarding" replace />;
  }
  return <Navigate to="/main/feed" replace />;
}

function AppRoutes() {
  const { user, loading, isPolicyAccepted } = useAuth();

  useStartParamRedirect();

  useEffect(() => {
    resetDocumentThemeOverrides();
    if (!isTelegramWebAppEnvironment()) return undefined;
    const tg = window.Telegram?.WebApp;
    if (!tg) return undefined;
    tg.ready();
    tg.expand();
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|webOS/i.test(navigator.userAgent);
    if (isMobile) {
      tg.requestFullscreen?.();
      tg.disableVerticalSwipes?.();
      tg.lockOrientation?.();
    }
    document.documentElement.style.setProperty('--ios-safe-area-inset-top', '0px');
    return undefined;
  }, []);

  if (loading && !user) {
    return (
      <div className="app app--loading">
        <p>Загрузка…</p>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/browser-login/qr" element={<BrowserLoginQrPage />} />
      <Route
        path="/browser-login"
        element={user ? <Navigate to="/" replace /> : <BrowserLoginPage />}
      />
      <Route path="/" element={<RootRedirect />} />
      <Route
        path="/onboarding"
        element={
          user && !isPolicyAccepted ? (
            <OnboardingPage />
          ) : user && isPolicyAccepted ? (
            <Navigate to="/main/feed" replace />
          ) : !hasTelegramWebAppInitData() ? (
            <Navigate to="/main/catalog" replace />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route path="/policy" element={<PolicyPage />} />
      <Route path="/public-offer" element={<PublicOfferPage />} />
      <Route path="/main" element={<MainPage />}>
        <Route
          index
          element={
            <RequireWebUser>
              <FeedPage />
            </RequireWebUser>
          }
        />
        <Route
          path="feed"
          element={
            <RequireWebUser>
              <FeedPage />
            </RequireWebUser>
          }
        />
        <Route
          path="cart"
          element={
            <RequireWebUser>
              <CartPage />
            </RequireWebUser>
          }
        />
        <Route
          path="checkout"
          element={
            <RequireWebUser>
              <CheckoutPage />
            </RequireWebUser>
          }
        />
        <Route
          path="fitting/gallery"
          element={
            <RequireWebUser>
              <TryOnGalleryPage />
            </RequireWebUser>
          }
        />
        <Route
          path="fitting"
          element={
            <RequireWebUser>
              <FittingPage />
            </RequireWebUser>
          }
        />
        <Route path="catalog" element={<CatalogPage />} />
        <Route path="catalog/:itemId" element={<CatalogItemPage />} />
        <Route path="catalog/:itemId/reviews" element={<ItemReviewsPage />} />
        <Route
          path="profile"
          element={
            <RequireWebUser>
              <ProfilePage />
            </RequireWebUser>
          }
        />
        <Route
          path="profile/link-account"
          element={
            <RequireWebUser>
              <LinkAccountPage />
            </RequireWebUser>
          }
        />
        <Route
          path="profile/link-account/qr"
          element={
            <RequireWebUser>
              <BrowserLoginQrPage />
            </RequireWebUser>
          }
        />
        <Route
          path="profile/liked"
          element={
            <RequireWebUser>
              <LikedPage />
            </RequireWebUser>
          }
        />
        <Route
          path="profile/addresses"
          element={
            <RequireWebUser>
              <AddressesPage />
            </RequireWebUser>
          }
        />
        <Route
          path="profile/addresses/edit"
          element={
            <RequireWebUser>
              <AddressEditPage />
            </RequireWebUser>
          }
        />
        <Route
          path="profile/addresses/cdek-pvz"
          element={
            <RequireWebUser>
              <CdekPickupStubPage />
            </RequireWebUser>
          }
        />
        <Route
          path="profile/referral"
          element={
            <RequireWebUser>
              <ReferralDashboardPage />
            </RequireWebUser>
          }
        />
        <Route
          path="profile/sessions"
          element={
            <RequireWebUser>
              <SessionsPage />
            </RequireWebUser>
          }
        />
        <Route
          path="profile/orders"
          element={
            <RequireWebUser>
              <OrdersPage />
            </RequireWebUser>
          }
        />
        <Route
          path="profile/orders/:orderId/review"
          element={
            <RequireWebUser>
              <LeaveReviewPage />
            </RequireWebUser>
          }
        />
        <Route
          path="profile/support"
          element={
            <RequireWebUser>
              <SupportChatPage />
            </RequireWebUser>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ProductAnalyticsBoot() {
  useEffect(() => {
    initProductAnalytics();
    return () => shutdownProductAnalytics();
  }, []);
  return null;
}

function MiniappShell() {
  const { guestHtml } = useAuth();
  if (guestHtml != null) {
    return (
      <iframe
        title="Сообщение"
        srcDoc={guestHtml}
        style={{ border: 0, width: '100%', minHeight: '100vh', display: 'block' }}
      />
    );
  }
  return (
    <>
      <ProductAnalyticsBoot />
      {/* ANCHOR: vk-group-announcement-modal — рендер (удалить при снятии анонса VK) */}
      <VkGroupAnnouncementBoot />
      <AppRoutes />
    </>
  );
}

function App() {
  return (
    <HashRouter>
      <AuthProvider>
        <TabBarVisibilityProvider>
          <div className="app-viewport">
            <div className="app">
              <MiniappShell />
            </div>
          </div>
        </TabBarVisibilityProvider>
      </AuthProvider>
    </HashRouter>
  );
}

export default App;
